import { useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';

import {
    type LocationProviderConfig,
    type PenRef,
    type ProviderConfigError,
    type ProviderFieldError,
    type ProviderGroup,
    type ProviderSchema,
    parseProviderConfigError,
    type SecretName,
    saveRequestFits,
    type TestResult,
    useSaveProviderConfig,
} from '@/domain/providerConfig';
import { useClientErrors } from '@/hooks/useClientErrors';
import { SERVER_ERROR } from '@/routes/shared/ProviderConfig/form/errorTypes';
import {
    fieldMessagesFor,
    serverErrorView,
} from '@/routes/shared/ProviderConfig/form/serverErrors';

import type { FieldConflict } from '../form/conflictMerge';
import { penSecretOutlook, type SecretOutlook } from '../form/effectiveRows';
import {
    type DiffSummary,
    diffForm,
    type FormDiff,
    isEmptyDiff,
    needsConnectionTest,
    summarizeDiff,
} from '../form/formDiff';
import { editableSchema, type ProviderFormValues, toFormValues } from '../form/formModel';
import { clientFormErrors } from '../form/formRules';
import { buildSaveInput } from '../form/saveInput';

import { applyFieldErrors, isFormPath, ruleErrorPaths } from './formPaths';
import { splitQuietRequired } from './quietRequired';
import { type SaveBlocker, saveBlocker } from './saveBlocker';
import { useConnectionTest } from './useConnectionTest';
import { useFormBase } from './useFormBase';

export interface ProviderCardFormArgs {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    locationId: number;
    providerName: string;
    isOptoScaleAdmin: boolean;
    /** Told the diff a save stored, before the form starts over from the fresh rows. */
    onSaved?: (saved: FormDiff) => void;
    refetch: () => Promise<LocationProviderConfig | null>;
    /** Fields whose rule fails but whose editor holds the marks back (HeldBackFields). */
    heldBack?: ReadonlySet<string>;
}

export type { SaveBlocker };
export type SaveOutcome = 'saved' | 'conflict' | 'invalid' | 'failed';

export interface ProviderCardForm {
    form: UseFormReturn<ProviderFormValues>;
    /** The rows the form was built from; the sections show these. */
    baseGroup: ProviderGroup | null;
    diff: FormDiff;
    /** The save rules' errors at form paths, as the fields show them. */
    clientErrors: ProviderFieldError[];
    secretOutlook: ReadonlyMap<number, Readonly<Record<SecretName, SecretOutlook>>>;
    summary: DiffSummary;
    dirty: boolean;
    needsTest: boolean;
    test: {
        result: TestResult | null;
        /** The connection changed since `result` was tested. */
        stale: boolean;
        tokenValid: boolean;
        testing: boolean;
        error: ProviderConfigError | null;
    };
    runTest: (cageIds: number[]) => Promise<void>;
    testOverride: boolean;
    setTestOverride: (value: boolean) => void;
    saveBlockedBy: SaveBlocker | null;
    saving: boolean;
    save: () => Promise<SaveOutcome>;
    saveError: ProviderConfigError | null;
    conflicts: FieldConflict[];
    stale: boolean;
    reloadKeepingEdits: () => Promise<void>;
}

const NONE_HELD: ReadonlySet<string> = new Set();

/** One provider's card: the form, its diff, the connection test and the save. */
export function useProviderCardForm(args: ProviderCardFormArgs): ProviderCardForm {
    const { schema, group, pens, locationId, providerName, isOptoScaleAdmin } = args;
    const editable = editableSchema(schema, isOptoScaleAdmin);
    const [initial] = useState(() => toFormValues(schema, group, pens));
    const form = useForm<ProviderFormValues>({ defaultValues: initial, mode: 'onChange' });
    const values = form.watch();
    const base = useFormBase({ ...args, form, initial });
    const diff = diffForm(editable, base.values, values);
    const dirty = !isEmptyDiff(diff);
    const clientErrors = clientFormErrors(editable, base.group, diff, { isOptoScaleAdmin });
    const marked = splitQuietRequired(form, clientErrors, isFormPath);
    useClientErrors(form, marked.shown, isFormPath);

    const connectionTest = useConnectionTest(locationId, providerName, diff, form);
    const {
        save: saveConfig,
        isSaving,
        reset: resetSaveRequest,
    } = useSaveProviderConfig(locationId, providerName);
    const [testOverride, setTestOverride] = useState(false);
    const [saveError, setSaveError] = useState<ProviderConfigError | null>(null);

    const needsTest = needsConnectionTest(diff);
    const overridden = isOptoScaleAdmin && testOverride;
    const saveBlockedBy = saveBlocker({
        dirty,
        ruleErrorPaths: ruleErrorPaths(form.formState.errors),
        clientErrors: marked.shown,
        requiredMissing: marked.quiet.length > 0,
        heldBack: args.heldBack ?? NONE_HELD,
        testMissing: needsTest && connectionTest.token === null && !overridden,
    });

    async function save(): Promise<SaveOutcome> {
        if (saveBlockedBy !== null) return 'invalid';
        const token = needsTest ? connectionTest.token : null;
        const extras =
            token !== null
                ? { testToken: token }
                : needsTest && overridden
                  ? { testOverride: true }
                  : {};
        setSaveError(null);
        function startOver(fresh: LocationProviderConfig): 'saved' {
            args.onSaved?.(diff);
            base.rebase(fresh);
            connectionTest.clear();
            setTestOverride(false);
            return 'saved';
        }
        const input = buildSaveInput(diff, base.group, extras);
        if (!saveRequestFits(input)) {
            setSaveError({ kind: 'tooLarge' });
            return 'failed';
        }
        try {
            return startOver(await saveConfig(input));
        } catch (failure) {
            const parsed = parseProviderConfigError(failure);
            if (parsed.kind === 'savedUnread') {
                // A retry would conflict with the change it already made.
                const fresh = await args.refetch();
                if (fresh !== null) return startOver(fresh);
            }
            setSaveError(parsed);
            if (parsed.kind === 'stale' || parsed.kind === 'alreadyConnected')
                return (await base.mergeFresh()) ? 'conflict' : 'failed';
            if (parsed.kind === 'testRequired') connectionTest.clear();
            const fields = fieldMessagesFor(serverErrorView(parsed).fields, isOptoScaleAdmin);
            const applied = applyFieldErrors(form, fields, SERVER_ERROR);
            return parsed.kind === 'invalid' || applied.length > 0 ? 'invalid' : 'failed';
        } finally {
            // The outcome is copied above; the cached request still holds typed secrets.
            resetSaveRequest();
        }
    }

    async function reloadKeepingEdits(): Promise<void> {
        await base.mergeFresh();
    }

    return {
        form,
        baseGroup: base.group,
        diff,
        clientErrors,
        secretOutlook: penSecretOutlook(editable, base.group, diff),
        summary: summarizeDiff(diff, base.group),
        dirty,
        needsTest,
        test: {
            result: connectionTest.result,
            stale: connectionTest.stale,
            tokenValid: connectionTest.token !== null,
            testing: connectionTest.testing,
            error: connectionTest.error,
        },
        runTest: connectionTest.run,
        testOverride: overridden,
        setTestOverride,
        saveBlockedBy,
        saving: isSaving,
        save,
        saveError,
        conflicts: base.conflicts,
        stale: base.stale,
        reloadKeepingEdits,
    };
}
